// Live — who is moving, who is stuck, what matters (owner, 2026-10-10).
//
// « Today it is a log: 30 rows that look the same. » Rebuilt as: one live
// line (what the agent is doing this second), three numbers, the journey map
// (Écrit · Photos · Album · Aperçu · Validé · Payé), « pourquoi » on any chip,
// a feed with the agent's mistakes flagged, a replay of any of the last 14
// days, and a soft tick (off by default) when a client reaches Validé or Payé.
//
// ⚠️ REAL DATA ONLY. Every chip, colour, number and line is read from the
// board, the Inbox and the decision log — nothing is a model's guess.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { pilotageApi, type JournalGroup } from '@/lib/api';
import { usePilotage } from './usePilotage';
import { useIsPhone } from './phone';
import { agoLabel } from './workbench';
import { chipsAt, chipsNow, threeNumbers, waitShort, type Chip } from './live/journey';
import { JourneyMap } from './live/JourneyMap';
import { WhyPanel } from './live/WhyPanel';
import { LiveJournal } from './live/LiveJournal';
import { ReplayBar } from './live/ReplayBar';

const SOUND_KEY = 'storiz.admin.liveSound';
const readSound = () => { try { return localStorage.getItem(SOUND_KEY) === 'on'; } catch { return false; } };

/** A soft tick — no file, no network. Silent if the browser refuses audio. */
function tick() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.22);
    window.setTimeout(() => { void ctx.close(); }, 400);
  } catch { /* no sound */ }
}

type ListKind = 'alone' | 'red' | 'amber';

export function LiveTab({ onOpenChat, onOpenInbox }: { onOpenChat: (chatId: string) => void; onOpenInbox: () => void }) {
  const { t } = useT();
  const isPhone = useIsPhone();
  const { data, isLoading, isError } = usePilotage(8_000);
  const { data: nowLine } = useQuery({
    queryKey: ['pilotage', 'now'],
    queryFn: () => pilotageApi.now(),
    refetchInterval: 4_000,
    refetchIntervalInBackground: false,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  const [dayOffset, setDayOffset] = useState(0);
  const [at, setAt] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [list, setList] = useState<ListKind | null>(null);
  const [sound, setSound] = useState(readSound);
  const toggleSound = () => setSound((s) => {
    try { localStorage.setItem(SOUND_KEY, s ? 'off' : 'on'); } catch { /* this visit only */ }
    return !s;
  });

  const clients = useMemo(() => data?.pipeline?.clients ?? [], [data]);
  const items = useMemo(() => data?.inbox?.items ?? [], [data]);
  const live = data?.live;

  // The agent acted for her in the last two minutes — the blue chips.
  const acting = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of live?.feed ?? []) {
      if (!f.chatId || f.kind === 'order') continue;
      const ts = Date.parse(f.at);
      if (Number.isFinite(ts)) m.set(f.chatId, Math.max(m.get(f.chatId) ?? 0, ts));
    }
    if (nowLine?.chatId && nowLine.state !== 'waiting') m.set(nowLine.chatId, Date.now());
    return m;
  }, [live, nowLine]);

  const liveChips = useMemo(() => chipsNow({ clients, items, acting, nowMs: now }), [clients, items, acting, now]);
  const chips = at == null ? liveChips : chipsAt(clients, at);
  const nums = threeNumbers(liveChips, items, live?.today.handledAlone);
  const selected = chips.find((c) => c.key === selectedKey) ?? liveChips.find((c) => c.key === selectedKey) ?? null;
  const chipOfChat = (chatId: string | null) => (chatId ? liveChips.find((c) => c.client.chatIds.includes(chatId)) : undefined);

  if (isLoading) return <p className="text-[12.5px] text-text-tertiary">{t('Chargement…')}</p>;
  if (isError || !data) return <p className="text-[12.5px] text-status-waiting">{t('Impossible de charger le direct.')}</p>;
  if (!live || !data.pipeline) return <p className="text-[12.5px] text-text-tertiary">{t('Le direct arrive avec la nouvelle version du serveur.')}</p>;

  const openChip = (c: Chip) => { setSelectedKey(c.key); setList(null); };
  const openChat = (chatId: string | null) => {
    const c = chipOfChat(chatId);
    if (c) openChip(c);
    else if (chatId) onOpenChat(chatId);
  };

  // ─── 1. THE LIVE LINE ───────────────────────────────────────────────────
  const working = nowLine && nowLine.state !== 'waiting';
  const strip = live.status.stale ? (
    <span className="text-status-stuck font-medium">
      {t('L’agent ne bouge plus — un client attend depuis {w}. Vérifie la ligne WhatsApp et les crédits IA.', { w: agoLabel(live.status.staleSinceIso, now) })}
    </span>
  ) : working ? (
    <span className="text-text-primary">
      {nowLine!.state === 'writing' ? t('L’agent écrit à') : t('L’agent vient d’écrire à')}{' '}
      <button type="button" onClick={() => openChat(nowLine!.chatId)} className="font-semibold text-info underline-offset-4 hover:underline cursor-pointer">{nowLine!.name}</button>
      {nowLine!.state === 'writing' ? '…' : ''}
    </span>
  ) : (
    <span className="text-text-secondary">{t('L’agent attend un message…')}</span>
  );

  // ─── 2. THE THREE NUMBERS ───────────────────────────────────────────────
  const numCard = (kind: ListKind, value: number, label: string, tone: string) => (
    <button type="button" onClick={() => setList(list === kind ? null : kind)} aria-expanded={list === kind}
      className={`text-left rounded-xl border px-4 py-3 cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${tone} ${list === kind ? 'ring-2 ring-brand-500' : ''} ${isPhone ? 'flex flex-col gap-0.5' : 'flex items-baseline gap-3'}`}>
      <span className={`font-heading tabular-nums leading-none ${isPhone ? 'text-[26px]' : 'text-[32px]'}`}>{value}</span>
      <span className={`${isPhone ? 'text-[11.5px] leading-tight' : 'text-[13px]'}`}>{label}</span>
    </button>
  );
  const listRows: Array<{ key: string; name: string; sub: string; onClick: () => void; tone: string }> = list === 'red'
    ? [...nums.red].sort((a, b) => Date.parse(String(a.sinceIso)) - Date.parse(String(b.sinceIso))).map((c) => ({
      key: c.key, name: c.name, sub: `${t(c.client.stageLabel)} · ${waitShort(c.sinceIso, now)}`, onClick: () => openChip(c), tone: 'text-status-stuck',
    }))
    : list === 'amber'
      ? items.map((i) => {
        const c = liveChips.find((x) => (i.orderId && x.orderId === i.orderId) || (i.chatId && x.client.chatIds.includes(i.chatId)));
        return { key: i.key, name: c?.name ?? i.name ?? t('Sans nom'), sub: `${t(i.title)} · ${waitShort(i.sinceIso, now)}`, onClick: () => (c ? openChip(c) : onOpenInbox()), tone: 'text-status-waiting' };
      })
      : list === 'alone'
        ? (live.handledAloneChats ?? []).map((id) => {
          const c = chipOfChat(id);
          const g = live.journal?.find((x) => x.chatId === id);
          return { key: id, name: c?.name ?? g?.name ?? t('Sans nom'), sub: g?.entries[0] ? t(g.entries[0].text) : '', onClick: () => openChat(id), tone: 'text-text-primary' };
        })
        : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-bg-card px-4 py-2.5 text-[13px]">
        <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${live.status.stale ? 'bg-status-stuck-solid' : working ? 'bg-info jm-pulse text-info' : 'bg-border-strong'}`} />
        <p className="flex-1 min-w-0 truncate" aria-live="polite">{strip}</p>
        <button type="button" onClick={toggleSound} aria-pressed={sound}
          className="shrink-0 inline-flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer min-h-[36px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg px-1.5">
          {t('Son')}
          <span aria-hidden className={`relative h-[18px] w-[30px] rounded-full transition-colors duration-150 ${sound ? 'bg-brand-500' : 'bg-border-strong'}`}>
            <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-bg-card transition-transform duration-150 ${sound ? 'translate-x-[14px]' : 'translate-x-[2px]'}`} />
          </span>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {numCard('alone', nums.handledAlone, t('gérés seul aujourd’hui'), 'border-border-subtle bg-bg-card text-text-primary')}
        {numCard('red', nums.waitingOnUs, t('attendent sur nous'), 'border-status-stuck-border bg-status-stuck-bg text-status-stuck')}
        {numCard('amber', nums.needsYou, t('ont besoin de toi'), 'border-status-waiting-border bg-status-waiting-bg text-status-waiting')}
      </div>

      {list && (
        <div className="rounded-xl border border-border-subtle bg-bg-card overflow-hidden">
          {!listRows.length && <p className="px-4 py-3 text-[12.5px] text-text-tertiary">{t('Personne pour l’instant.')}</p>}
          <ul className="max-h-[240px] overflow-y-auto divide-y divide-border-subtle">
            {listRows.map((r) => (
              <li key={r.key}>
                <button type="button" onClick={r.onClick}
                  className="w-full text-left flex items-center gap-3 px-4 py-2.5 min-h-[44px] cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500">
                  <b className={`text-[13px] font-semibold truncate ${r.tone}`}>{r.name}</b>
                  <span className="text-[12px] text-text-tertiary truncate">{r.sub}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ReplayBar now={now} dayOffset={dayOffset} setDayOffset={setDayOffset} at={at} setAt={setAt}
        playing={playing} setPlaying={setPlaying} compact={isPhone} />

      <JourneyMap
        chips={chips}
        now={at ?? now}
        selectedKey={selectedKey}
        onSelect={openChip}
        isPhone={isPhone}
        onArrive={() => { if (sound && at == null) tick(); }}
      />

      <LiveJournal groups={live.journal ?? []} onSelect={(g: JournalGroup) => openChat(g.chatId)} />

      {selected && (
        <WhyPanel chip={selected} now={now} full={isPhone} onClose={() => setSelectedKey(null)}
          onOpenChat={(id) => { setSelectedKey(null); onOpenChat(id); }} />
      )}
    </div>
  );
}
